import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import IconDeepResearchText from '@components/Images/DeepResearch/IconDeepResearchText';
import { getImgixSrc } from '@utils/images';

import ShareForm from './ShareForm';

const PERSONAL_BRIEFING_PHONE_HALF_IMG = 'personal_briefing/phone_pb_half.gif';
const PERSONAL_BRIEFING_PHONE_FULL_IMG = 'personal_briefing/phone_pb_full.gif';

interface SharePersonalBriefingPageProps {
  aiCohortNotificationId: number;
}

const SharePersonalBriefingPage: React.FC<SharePersonalBriefingPageProps> = ({
  aiCohortNotificationId = null,
}) => {
  const queryClient = new QueryClient();
  const phoneImgUrlHalf = getImgixSrc({
    path: PERSONAL_BRIEFING_PHONE_HALF_IMG,
    isEnvDependent: false,
  });
  const phoneImgUrlFull = getImgixSrc({
    path: PERSONAL_BRIEFING_PHONE_FULL_IMG,
    isEnvDependent: false,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (isSubmitted) {
    return (
      <div id="ti-content" className="relative flex-grow">
        <div className="m-auto max-w-container-lg">
          <div className="grid grid-cols-12 gap-56">
            <div className="col-span-8 lg:col-start-3">
              <div className="flex flex-col items-center">
                {/* Header */}
                <h1 className="m-0 w-full break-words text-center font-suisse-works text-headline-xs text-grayscale-charcoal sm:text-headline-xl">
                  Thank you for sharing your Personal Briefing
                </h1>

                {/* Sub-header */}
                <div className="mb-24 flex">
                  <div className="m-0 mr-2 self-end font-suisse-works text-body-md font-normal sm:text-overline-lg md:text-headline-xxs">
                    Powered by
                  </div>
                  <IconDeepResearchText
                    className="mb-3 ml-12 w-[138px] sm:w-[164px] md:w-[187px]"
                    aria-label="Deep Research"
                  />
                </div>

                <div className="flex flex-col items-center">
                  <img
                    src={phoneImgUrlHalf}
                    className="w-4/5 lg:-ml-12"
                    alt="Personal briefing phone"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ti-content" className="relative flex-grow">
      <div className="m-auto max-w-container-lg">
        <div className="grid grid-cols-12">
          <div className="col-span-12 px-32 pb-80 lg:col-span-8 lg:px-0 lg:pb-0 lg:pr-80">
            {/* Header */}
            <h1 className="m-0 w-full break-words font-suisse-works text-headline-xs text-grayscale-charcoal sm:text-headline-xl">
              Share your Personal Briefing
            </h1>

            {/* Sub-header */}
            <div className="flex">
              <div className="m-0 mr-2 self-end font-suisse-works text-body-md font-normal sm:text-overline-lg md:text-headline-xxs">
                Powered by
              </div>
              <IconDeepResearchText
                className="mb-3 ml-12 w-[138px] sm:w-[164px] md:w-[187px]"
                aria-label="Deep Research"
              />
            </div>

            {/* explanation */}
            <p className="font-suisse-neue text-disclosure-xl">
              Don’t just share articles, share intelligence. Your Deep Research-powered Personal
              Briefing delivers a weekly, personalized summary of critical insights to keep your
              network aligned and informed.
            </p>

            {/* Form */}
            {aiCohortNotificationId ? (
              <QueryClientProvider client={queryClient}>
                <ShareForm
                  aiCohortNotificationId={aiCohortNotificationId}
                  onSuccess={() => setIsSubmitted(true)}
                />
              </QueryClientProvider>
            ) : (
              <div>
                Sorry, there was an issue with your share link. Is there a more recent one you can
                use?
              </div>
            )}
          </div>
          <div className="col-span-12 px-32 lg:col-span-4 lg:px-0">
            <div className="flex justify-center">
              <img
                srcSet={`${phoneImgUrlHalf} 600w, ${phoneImgUrlFull} 1200w`}
                sizes="(max-width: 768px) 600px, 1200px"
                className="-ml-12 w-4/5"
                alt="Personal briefing on a phone screen"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePersonalBriefingPage;
